/* eslint-disable jsx-a11y/media-has-caption */
'use client';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { textOlive } from '@osmosis/tailwind-ui/styles/text';
import { bg4 } from '@osmosis/tailwind-ui/styles/background';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';

import { faCirclePlay } from '@fortawesome/pro-regular-svg-icons/faCirclePlay';
import { DenyReason, VideoType } from '@osmosis/shared/types/learn';

import VideoPoster from './VideoPoster';
import VideoPlaceholder from './VideoPlaceholder';
import { Props as VideoContainerProps } from './VideoContainer';
import { useLearnAccess } from '../../context/LearnAccessContext';
import { learnVideoTracking } from '../../lib/tracking';
import { useUserContext } from '../../context/UserContext';
import useVideoSrc from '../../hooks/useVideoSrc';
import { useVideoThumbnail } from '@osmosis/nextjs-components/hooks/useVideoThumbnail';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

// The player's not-started overlay stops above the control bar
// (see overlayNotStarted in VideoOverlay). The embed placeholder tints and
// centres its play button over the same area, or the blue block and the icon
// shift when the player takes over.
const OVERLAY_BOTTOM_GAP = 70;

const VideoContainer = dynamic(
  async () => {
    const { VideoContainer } = await import('./VideoContainer');
    return VideoContainer;
  },
  {
    ssr: false,
  },
);

/**
 * VideoContainerWrapper element.
 */
/** Returns true if autoplay should be active based on localStorage flags. */
const getAutoplayFromStorage = () => {
  if (typeof window === 'undefined') return false;
  return (
    localStorage.getItem('autoplayLearnVideos') === 'true' &&
    localStorage.getItem('idleStopPlay') !== 'true'
  );
};

type VideoContainerWrapperProps = VideoContainerProps & {
  classNames?: {
    container?: string;
    controlBar?: string;
  };
};

/**
 * VideoContainerWrapper element.
 */
export default function VideoContainerWrapper(
  props: VideoContainerWrapperProps,
) {
  const [playBtnClicked, setPlayBtnClicked] = useState(getAutoplayFromStorage);
  // When autoplay is on, show VideoContainer immediately (don't wait for idle callback)
  const [actualVideoVisible, setActualVideoVisible] = useState(
    getAutoplayFromStorage,
  );

  const intl = useIntl();
  const { denyReason } = useLearnAccess();
  const isExpired = denyReason === DenyReason.expired;

  const { videoType } = useLearnAccess();
  const { user, isUserLoaded } = useUserContext();

  const poster = useVideoThumbnail(props.video);
  const srcData = useVideoSrc(props.video);

  // Gate on isUserLoaded: learnVideoTracking emits an "immediate prime
  // video lockout" event when `user` is null, so firing before user data
  // resolves would mis-tag logged-in users as logged-out.
  useEffect(() => {
    if (!isUserLoaded) return;
    if (videoType !== VideoType.video) return;
    learnVideoTracking(
      {
        isPrimeOnly: props.isPrimeOnly,
        upNext: props.upNext,
        breadcrumbsv2: props.breadcrumbsv2,
        learnNodeID: props.learnNodeID,
        name: props.name,
        flashcards: props.flashcards,
        questions: props.questions,
      },
      user,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoaded]);

  useEffect(() => {
    if (!user) return;
    if (!user.isFaculty) return;
    const autoplay = localStorage.getItem('autoplayLearnVideos') === 'true';
    const facultyOk =
      localStorage.getItem('facultyAutoplayReset') === 'autoplay';
    const notIdle = localStorage.getItem('idleStopPlay') !== 'true';
    if (!autoplay || !facultyOk || !notIdle) {
      setPlayBtnClicked(false);
    }
  }, [user]);

  useEffect(() => {
    // Check if requestIdleCallback is supported
    if (videoType === VideoType.video && !isExpired) {
      if ('requestIdleCallback' in window) {
        // Schedule a task to run when the browser is idle
        const handle = window.requestIdleCallback(
          () => {
            !actualVideoVisible && setActualVideoVisible(true);
          },
          { timeout: 2000 },
        ); // Optional: timeout for critical tasks (e.g., run within 2 seconds)

        // Cleanup function to cancel the idle callback if the component unmounts
        return () => {
          if (handle) {
            window.cancelIdleCallback(handle);
          }
        };
      } else {
        // Fallback for browsers that don't support requestIdleCallback
        const timer = setTimeout(() => {
          setActualVideoVisible(true);
        }, 0);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const loadActualVideoComponent = () => {
    setPlayBtnClicked(true);
    setActualVideoVisible(true);
  };

  if (videoType !== VideoType.video) {
    return (
      <Box
        className={cn(
          props.isEmbed
            ? 'relative h-full w-full'
            : 'relative mb-2 aspect-video h-[50vw] w-full xl:h-[min(665px,40vw)]',
          props.classNames?.container,
        )}
      >
        <VideoPoster poster={poster} />
        <Box
          className={`absolute bottom-0 left-0 right-0 top-0 h-auto ${bg4} flex items-center justify-center opacity-[.96]`}
        >
          {videoType === VideoType.dailyVideoLimitReached && (
            <H2
              className={`text-center ${textOlive}`}
              data-testid="video-player.dailyVideoLimitReached"
            >
              <FormattedMessage
                id="video-player.dailyVideoLimitReached"
                defaultMessage="You've reached your maximum daily number of video views for a single account. Please contact support@osmosis.org if you think this is an error."
              />
            </H2>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={cn(
        props.isEmbed
          ? 'relative h-full w-full'
          : 'relative mb-2 aspect-video h-[50vw] w-full xl:h-[min(665px,40vw)]',
        props.classNames?.container,
      )}
    >
      {actualVideoVisible ? (
        <VideoContainer {...props} autoplay={playBtnClicked} />
      ) : (
        <Box
          data-testid="video-container-wrapper"
          // The embed sizes the video to its container, so the 665px cap would
          // letterbox the poster differently than the player that replaces it
          className={cn(
            'group relative h-full w-full [&>img]:!relative',
            !props.isEmbed && 'max-h-[665px]',
          )}
          onClick={loadActualVideoComponent}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              loadActualVideoComponent();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={intl.formatMessage({
            id: 'video-player.playVideo',
            defaultMessage: 'Play video',
          })}
        >
          <video
            data-testid="video-container-wrapper-plain-video"
            // The player draws its poster with object-contain over the whole
            // container, so cropping here makes the thumbnail jump on swap
            className={cn(
              'h-full w-full',
              props.isEmbed ? 'object-contain' : 'object-cover',
            )}
            preload="metadata"
            poster={poster}
            src={srcData.src || undefined}
          />
          <VideoPlaceholder
            style={props.isEmbed ? { bottom: OVERLAY_BOTTOM_GAP } : undefined}
          />
          <Box
            className={cn(
              'absolute',
              props.isEmbed
                ? 'inset-x-0 top-0 flex items-center justify-center'
                : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform',
            )}
            style={props.isEmbed ? { bottom: OVERLAY_BOTTOM_GAP } : undefined}
          >
            <IconButton
              rounded
              // In the embed this button is swapped for the player's own play
              // overlay, which is a fixed text-7xl, so match it or the icon
              // visibly resizes mid swap
              className={cn(
                'transition-[font-size] duration-200 ease-in-out',
                props.isEmbed ? 'text-7xl' : 'text-6xl group-hover:text-7xl',
              )}
              color="transparent"
              data-testid="video-container-wrapper-play-button"
              icon={<Icon icon={faCirclePlay} />}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
